import { supabase } from "@/lib/supabase";
import type { Flashcard, InsertFlashcard, FlashcardReview } from "@shared/schema";

export type TimeFrame = "week" | "month" | "year";

export const flashcardService = {
  async getFlashcards(userId: string, timeFrame?: TimeFrame): Promise<Flashcard[]> {
    let query = supabase
      .from("flashcards")
      .select("id, user_id, front, back, source_type, source_id, space_id, topic_id, tags, difficulty, interval, ease_factor, repetitions, next_review_date, last_review_date, created_at, updated_at")
      .eq("user_id", userId);

    if (timeFrame) {
      const startDate = new Date();
      switch (timeFrame) {
        case "week":
          startDate.setDate(startDate.getDate() - 7);
          break;
        case "month":
          startDate.setDate(startDate.getDate() - 30);
          break;
        case "year":
          startDate.setFullYear(startDate.getFullYear() - 1);
          break;
      }
      query = query.gte("created_at", startDate.toISOString());
    }

    const { data, error } = await query.order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching flashcards:", error);
      return [];
    }
    return data || [];
  },

  async getDueCards(userId: string, limit: number = 20): Promise<Flashcard[]> {
    const now = new Date().toISOString();
    
    const { data, error } = await supabase
      .from("flashcards")
      .select("id, user_id, front, back, source_type, source_id, space_id, topic_id, tags, difficulty, interval, ease_factor, repetitions, next_review_date, last_review_date, created_at, updated_at")
      .eq("user_id", userId)
      .lte("next_review_date", now)
      .order("next_review_date", { ascending: true })
      .limit(limit);

    if (error) {
      console.error("Error fetching due cards:", error);
      return [];
    }
    return data || [];
  },

  async getCardsByTimeFrame(userId: string, timeFrame: TimeFrame): Promise<Flashcard[]> {
    const startDate = new Date();
    
    switch (timeFrame) {
      case "week":
        startDate.setDate(startDate.getDate() - 7);
        break;
      case "month":
        startDate.setDate(startDate.getDate() - 30);
        break;
      case "year":
        startDate.setFullYear(startDate.getFullYear() - 1);
        break;
    }

    const { data, error } = await supabase
      .from("flashcards")
      .select("id, user_id, front, back, source_type, source_id, space_id, topic_id, tags, difficulty, interval, ease_factor, repetitions, next_review_date, last_review_date, created_at, updated_at")
      .eq("user_id", userId)
      .gte("last_review_date", startDate.toISOString())
      .order("last_review_date", { ascending: false });

    if (error) {
      console.error("Error fetching cards by time frame:", error);
      return [];
    }
    return data || [];
  },

  async createFlashcard(card: InsertFlashcard): Promise<Flashcard | null> {
    const { data, error } = await supabase
      .from("flashcards")
      .insert(card)
      .select()
      .single();

    if (error) {
      console.error("Error creating flashcard:", error);
      return null;
    }
    return data;
  },

  async updateFlashcard(id: string, updates: Partial<Flashcard>): Promise<Flashcard | null> {
    const { data, error } = await supabase
      .from("flashcards")
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq("id", id)
      .select()
      .single();

    if (error) {
      console.error("Error updating flashcard:", error);
      return null;
    }
    return data;
  },

  async deleteFlashcard(id: string): Promise<boolean> {
    const { error } = await supabase
      .from("flashcards")
      .delete()
      .eq("id", id);

    if (error) {
      console.error("Error deleting flashcard:", error);
      return false;
    }
    return true;
  },

  async recordReview(
    userId: string,
    flashcardId: string,
    quality: number,
    wasCorrect: boolean,
    responseTimeMs?: number
  ): Promise<void> {
    await supabase.from("flashcard_reviews").insert({
      user_id: userId,
      flashcard_id: flashcardId,
      quality: quality.toString(),
      was_correct: wasCorrect ? "true" : "false",
      response_time: responseTimeMs?.toString(),
    });

    const card = await this.getFlashcardById(flashcardId);
    if (!card) return;

    const { interval, easeFactor, repetitions } = this.calculateSM2(
      parseInt(card.interval || "1", 10),
      parseFloat(card.easeFactor || "2.5"),
      parseInt(card.repetitions || "0", 10),
      quality
    );

    const nextReviewDate = new Date();
    nextReviewDate.setDate(nextReviewDate.getDate() + interval);

    await this.updateFlashcard(flashcardId, {
      interval: interval.toString(),
      easeFactor: easeFactor.toString(),
      repetitions: repetitions.toString(),
      nextReviewDate: nextReviewDate,
      lastReviewDate: new Date(),
    });
  },

  async getFlashcardById(id: string): Promise<Flashcard | null> {
    const { data, error } = await supabase
      .from("flashcards")
      .select("id, user_id, front, back, source_type, source_id, space_id, topic_id, tags, difficulty, interval, ease_factor, repetitions, next_review_date, last_review_date, created_at, updated_at")
      .eq("id", id)
      .single();

    if (error) {
      console.error("Error fetching flashcard:", error);
      return null;
    }
    return data;
  },

  calculateSM2(
    currentInterval: number,
    currentEaseFactor: number,
    currentRepetitions: number,
    quality: number
  ): { interval: number; easeFactor: number; repetitions: number } {
    let newEaseFactor = currentEaseFactor + (0.1 - (5 - quality) * (0.08 + (5 - quality) * 0.02));
    newEaseFactor = Math.max(1.3, newEaseFactor);

    let newInterval: number;
    let newRepetitions: number;

    if (quality < 3) {
      newRepetitions = 0;
      newInterval = 1;
    } else {
      newRepetitions = currentRepetitions + 1;
      
      if (newRepetitions === 1) {
        newInterval = 1;
      } else if (newRepetitions === 2) {
        newInterval = 6;
      } else {
        newInterval = Math.round(currentInterval * newEaseFactor);
      }
    }

    return {
      interval: newInterval,
      easeFactor: Math.round(newEaseFactor * 100) / 100,
      repetitions: newRepetitions,
    };
  },

  async getReviewStats(userId: string, days: number = 7): Promise<{
    totalReviews: number;
    correctCount: number;
    averageQuality: number;
  }> {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    const { data, error } = await supabase
      .from("flashcard_reviews")
      .select("id, user_id, flashcard_id, quality, response_time, was_correct, created_at")
      .eq("user_id", userId)
      .gte("created_at", startDate.toISOString());

    if (error || !data) {
      return { totalReviews: 0, correctCount: 0, averageQuality: 0 };
    }

    const totalReviews = data.length;
    const correctCount = data.filter((r: FlashcardReview) => r.wasCorrect === "true").length;
    const totalQuality = data.reduce((sum: number, r: FlashcardReview) => sum + parseInt(r.quality, 10), 0);
    const averageQuality = totalReviews > 0 ? totalQuality / totalReviews : 0;

    return {
      totalReviews,
      correctCount,
      averageQuality: Math.round(averageQuality * 10) / 10,
    };
  },

  async getCardCounts(userId: string): Promise<{
    total: number;
    dueToday: number;
    thisWeek: number;
    thisMonth: number;
    thisYear: number;
  }> {
    const now = new Date();
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const yearAgo = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);

    const [total, dueToday, thisWeek, thisMonth, thisYear] = await Promise.all([
      supabase.from("flashcards").select("id", { count: "exact" }).eq("user_id", userId),
      supabase.from("flashcards").select("id", { count: "exact" }).eq("user_id", userId).lte("next_review_date", now.toISOString()),
      supabase.from("flashcards").select("id", { count: "exact" }).eq("user_id", userId).gte("created_at", weekAgo.toISOString()),
      supabase.from("flashcards").select("id", { count: "exact" }).eq("user_id", userId).gte("created_at", monthAgo.toISOString()),
      supabase.from("flashcards").select("id", { count: "exact" }).eq("user_id", userId).gte("created_at", yearAgo.toISOString()),
    ]);

    return {
      total: total.count || 0,
      dueToday: dueToday.count || 0,
      thisWeek: thisWeek.count || 0,
      thisMonth: thisMonth.count || 0,
      thisYear: thisYear.count || 0,
    };
  },
};
